import { useTranslation } from "react-i18next";
import Layout from "./common/Layout";
import Back from "./common/Back";
import OtpInput from "./common/OtpInput";
import { useEffect, useState } from "react";
import { useApi, useAppDispatch, useEmail } from "../store/hooks";
import Spinner from "./common/Spinner";
import Toast from "./common/Toast";
import { useNavigate } from "react-router-dom";
import { GoToTheNextStep } from "./Step";

const OtpCheck = () => {
  const { t } = useTranslation();
  const email = useEmail();

  const { confirmOtp, sendOtp } = useApi();
  const [otpValue, setOtpValue] = useState(null);
  const [isOtpInvalid, setIsOtpInvalid] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [canRequestOtp, setCanRequestOtp] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [showSentMessage, setShowSentMessage] = useState(false);
  const [showIsInvalidOtpMessage, setShowIsInvalidOtpMessage] = useState(false);
  const [showMaxAttemptsToast, setShowMaxAttemptsToast] = useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { getWorkflow } = useApi();

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0 && !canRequestOtp) {
      setCanRequestOtp(true);
    }
    return () => clearTimeout(timer);
  }, [countdown, canRequestOtp]);

  const handleOtpComplete = (result) => {
    setIsOtpInvalid(false);
    setShowIsInvalidOtpMessage(false);
    setOtpValue(result);
  };

  const OtpConfirm = async () => {
    setIsProcessing(true);
    try {
      await confirmOtp({ code: otpValue });
      await GoToTheNextStep(navigate, dispatch, getWorkflow);
    } catch (error) {
      switch (error.response?.data.Code) {
        case "OnboardingOtpCodeNotPermitted":
          navigate("/Failure");
          break;

        case "OnboardingOtpCodeInvalid":
          setIsOtpInvalid(true);
          setShowIsInvalidOtpMessage(true);
          setShowSentMessage(false);
          break;

        case "OnboardingOtpCodeMaxRetryReached":
          setShowMaxAttemptsToast(true);
          setShowIsInvalidOtpMessage(false);
          break;

        default:
          navigate("/Failure");
          break;
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmit = () => {
    if (canConfirmOtp()) {
      OtpConfirm();
    }
  };

  const otpCheckRequest = async () => {
    try {
      await sendOtp();
    } catch (error) {
      navigate("/Failure");
    }
  };

  const canConfirmOtp = () => {
    return !isProcessing && otpValue;
  };

  const handleNewRequest = () => {
    if (canRequestOtp) {
      setCanRequestOtp(false);
      setShowMaxAttemptsToast(false);
      setShowSentMessage(true);
      setCountdown(30);
      otpCheckRequest();
    }
  };

  return (
    <Layout>
      <div className="az-title">{t("otpCheck.title")}</div>

      <div className="az-subtitle">
        <span>
          {t("otpCheck.subtitle.part1")}
          <strong>{t("otpCheck.subtitle.email", { email: email })}</strong>
          {t("otpCheck.subtitle.part2")}
        </span>
      </div>

      <div className="mt-5">
        <OtpInput
          length={6}
          onComplete={handleOtpComplete}
          isWrong={isOtpInvalid}
          onChange={() => {
            setShowSentMessage(false);
            setShowMaxAttemptsToast(false);
          }}
          disabled={showMaxAttemptsToast}
          onSubmit={handleSubmit}
        />

        <div
          className={`az-btn text-uppercase mt-5 ${isProcessing ? "disabled" : ""} ${canConfirmOtp() ? "" : "disabled"} ${showMaxAttemptsToast ? "d-none" : ""}`}
          onClick={handleSubmit}
        >
          {isProcessing ? <Spinner size={24} /> : t("otpCheck.btn.label")}
        </div>

        <div
          className={`text-center mt-4 ${showMaxAttemptsToast ? "d-none" : ""}`}
        >
          <span className="not-received-span">{t("otpCheck.notReceived")}</span>
          <span
            onClick={handleNewRequest}
            className={`request-span ${!canRequestOtp ? "disabled" : ""} ${!canRequestOtp ? "cursor-not-allowed" : "cursor-pointer"}`}
          >
            {t("otpCheck.request")}
          </span>
          <span className={`request-span ${!canRequestOtp ? "disabled" : ""}`}>
            {!canRequestOtp && countdown + " sec"}
          </span>
        </div>

        {showIsInvalidOtpMessage && (
          <div className="error-message-container mt-4">
            <span className="error-message not-received-span">
              {t("otpCheck.invalidCode")}
            </span>
          </div>
        )}

        {showSentMessage && <Toast text={t("toast.email.sent")} />}
        {showMaxAttemptsToast && (
          <Toast
            text={t("toast.max.attempts")}
            isError={true}
            action={handleNewRequest}
          />
        )}
      </div>

      <Back message={t("back.label")} />
    </Layout>
  );
};

export default OtpCheck;
